import { z } from 'zod';

// Service contract types for missions
export const missionSchema = z.object({
  id: z.string(),
  title: z.string(),
  // Render-only: SL owns date formatting. A regex rejected the whole missions
  // list (items.map(toUiMission), no per-row catch) on any non-YYYY-MM-DD value.
  assigned_date: z.string().nullable(),
  assigned_day: z.enum(['Montag','Dienstag','Mittwoch','Donnerstag','Freitag']).optional().nullable(),
  region_id: z.string().optional().nullable(),
  // Render-only: a required .email() rejected the ENTIRE missions list on a
  // single null/blank/non-RFC owner (system-created missions). Kept required
  // string (type-stable); format-strictness dropped. Per-row catch in the
  // gateway additionally isolates any other bad row.
  owner_email: z.string(),
  status: z.enum(['planned','in_progress','completed','cancelled','draft']).optional().nullable(),
  route_polyline: z.string().optional().nullable(),
  route_distance_km: z.number().optional().nullable(),
  route_duration_min: z.number().optional().nullable(),
  route_variants: z.array(z.record(z.unknown())).optional().nullable(),
});

export const missionListSchema = z.object({
  items: z.array(missionSchema),
  next_cursor: z.string().optional().nullable(),
});

export const missionCreateSchema = z.object({
  title: z.string(),
  assigned_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  region_id: z.string().optional(),
  owner_email: z.string().email(),
  status: z.enum(['planned','in_progress','completed','cancelled','draft']).optional(),
  slot_index: z.number().optional(),
});

export const missionLeadSchema = z.object({
  // Required identity keys stay required (a stop with no id is genuinely
  // undecodable), but coerce: SL/Postgres may serialize bigint PKs/FKs as JSON
  // numbers. A bare z.string() THREW on a numeric id and the per-row catch in
  // normalizeMissionLeadArray silently skipped the stop — undercounting the
  // board (BUG-FE-MISSION-LEADS-SILENT-DROP). Coercion keeps the type stable
  // (string) without dropping a real row.
  id: z.coerce.string(),
  mission_id: z.coerce.string(),
  lead_id: z.coerce.string().optional(), // Accept both lead_id and unified_lead_id
  unified_lead_id: z.coerce.string().optional(), // Backend returns this field
  unified_lead: z.record(z.unknown()).optional().nullable(), // Enriched lead data from stops endpoint
  lead_briefing: z.record(z.unknown()).optional().nullable(),
  // Render-only ordering hint. A strict .int().nonnegative() rejected the WHOLE
  // stop row (and thus dropped it from the rendered list AND the count) on any
  // float / negative sentinel / string-encoded index SL might emit. The mapper
  // already coalesces to 0 (`order_index ?? 0`) and the UI re-derives ordering,
  // so accept any number (coercing string-encoded ints) without gating the row.
  order_index: z.coerce.number().optional().nullable(),
  planned_time: z.string().optional().nullable(),
  // Render-only (0 FE opinion): SL owns this lifecycle taxonomy. A strict enum
  // rejected the WHOLE stops list (missionLeadsListSchema = z.array, per-row
  // .parse in toUiMissionLead) on any new SL status — same passthrough doctrine
  // already applied to `anchor_source` below. mapLeadState pattern-matches known
  // values + falls back via its default branch.
  // Known: Planned|Visited|Skipped|queued|planned|visited|skipped|pending|bounced.
  status: z.string().optional().nullable(),
  // SL lifecycle state — authoritative when present (overrides status for bucket
  // sorting). Render-only: accept any SL state. Known: pending|queued|visited|
  // skipped|bounced|rejected.
  state: z.string().optional().nullable(),
  is_anchor: z.boolean().optional().nullable(),
  // Open-enum by design: SL may add new anchor sources (e.g. daemon-directives
  // tagged `commander_directive_2026_04_22` observed 2026-04-25) without a
  // coordinated FE schema bump. Parallel application of FE-SL-ERROR-ENVELOPE-V1's
  // passthrough doctrine — tolerate unknown values from external API, never gate
  // valid data because the FE schema lags behind. Callers that branch on this
  // field should pattern-match known values and treat unknowns as 'other'.
  anchor_source: z.string().optional().nullable(),
  preferred_time: z.string().optional().nullable(),
  // FR-FE-JOKER-AS-VISIT-FLOW-2026-06-10: a joker is a REAL mission_lead with
  // stop_type='joker' (NOT a row in a separate joker_stops table). Render-only
  // passthrough — open-string by design, SL owns the stop taxonomy. The board
  // derives joker identity from this field.
  stop_type: z.string().optional().nullable(),
  joker_reason: z.string().optional().nullable(),
  joker_source: z.string().optional().nullable(),
  // FR-FE-JOKER-COMPANY-NAME-2026-06-24: a joker / rep-discovered-company stop
  // has NO unified_lead and NO lead_briefing — its name lives server-side in
  // rep_discovered_companies, which SL copies onto this TOP-LEVEL field. Without
  // it the board falls back to the raw leadId / "Unbekannt". Render-only
  // passthrough — additive, absent on non-joker stops.
  company_name: z.string().optional().nullable(),
}).refine((data) => data.lead_id || data.unified_lead_id, {
  message: "Either lead_id or unified_lead_id must be present",
});

export const missionLeadsListSchema = z.array(missionLeadSchema);

